/**
 * 用語詳細の「基本情報」のタブの表と、その欄の定義・部品（参照 1 件の名前 ・ 状態遷移の表 ・
 * 取りうる値のリンク）。
 *
 * **基本情報は 欄 ／ 値 の表 1 枚にする**（Dhik 2026-09-29「用語も用語の基本情報と使われている場所の
 * 2 タブにして、基本情報の方は表形式にしてくれ」）。以前は定義と取りうる値の「用語」のタブと、
 * 形（種別・値のタイプ・属するクラス・単位・状態遷移）の「タイプ」のタブに分けていたが、1 つの語を
 * 確かめるのに 2 つのタブを行き来させていた。
 *
 * **欄の定義は 1 面の表と 3 列の表（案件モードの左右比較）で共有する。** 欄の並び・比べ方・描き方を
 * 別々に書くと、「変更前と比較」を押した前後で欄の並びや名前が変わる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の名前・定義・種別・値のタイプ・属するクラス・単位・取りうる値・状態遷移・揺れ表記を、欄 ／ 値 の表で描く欄の定義と部品を持つ。
 * @graph-connects TermSectionBadge [calls] 種別の札
 */
import type { TermDetailOutput, TermRef, TermTransition } from '@cortex/domain-spec-console';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { DiffSide } from '../components/spec/DiffSplitPanes.js';
import { specDiffCellClass } from '../components/spec/SpecDiffState.js';
import { TermLinkSuppress, TermText } from '../components/spec/TermText.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import type { PjSides } from './pj-compare-table.js';
import { pjLinkSearch } from './pj-link.js';
import { TERM_DETAIL_ROUTE } from './route-paths.js';
import { isTermShapeField, termFieldApplies } from './term-field-applicability.js';
import { termEnumKey, TermSectionBadge } from './TermsPage.js';

/** 翻訳関数。 @graph-connects none */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * 基本情報の欄 1 つの定義。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の欄 1 つの鍵・見出し・変更前後の比べ方・描き方を表す。
 * @graph-connects none
 */
export interface TermFieldSpec {
  /** 欄の鍵。 */
  key: string;
  /** 欄の見出し。 */
  label: ReactNode;
  /** 変更前と変更後を比べる文字列。見た目では比べない（`buildPjCompareRows`）。 */
  compareKey: (detail: TermDetailOutput) => string;
  /** 欄の値の描き方。 */
  render: (detail: TermDetailOutput) => ReactNode;
}

/**
 * 参照 1 件を比べるための文字列。番号と名前を含める。
 *
 * @param refValue 参照 1 件。未設定なら null
 * @returns 比べるための文字列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語が指す参照 1 件を、変更前と変更後で比べられる文字列へ写す。
 * @graph-connects none
 */
export function termRefCompareKey(refValue: TermRef | null): string {
  return refValue === null ? '' : `${String(refValue.id)}:${refValue.label}`;
}

/**
 * 基本情報の欄の定義を、並べる順に返す。名前 → 定義 → 備考 → 種別 → 値のタイプ → 属するクラス → 単位 →
 * 取りうる値 → 状態遷移 → 揺れ表記。
 *
 * **定義文に用語の自動リンクを掛け、自分自身へのリンクだけは抑える**（`TermLinkSuppress`）— 定義文には
 * 必ず自分の名前が出るので、抑えないと押しても同じ場所へ戻る導線が本文へ何度も挟まる。
 *
 * **備考は改行を残して描く**。人が画面から書く補足で、手順や表を行で書くためである。
 *
 * **取りうる値の並びの順に意味があることは、欄の見出しの ⓘ の title で断る**（並べ替えの「値の並び」
 * はこの順を使う）。
 *
 * @param t 翻訳関数
 * @param pj いま重ねている案件番号。値の用語へのリンクへ引き継ぐ。マスターを見ていれば undefined
 * @returns 欄の定義
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の基本情報の欄（名前・定義・備考・種別・値のタイプ・属するクラス・単位・取りうる値・状態遷移・揺れ表記）を、見出し・比べ方・描き方の組で並べる。
 * @graph-connects TermText [calls] 定義文の用語自動リンク
 * @graph-connects TermLinkSuppress [calls] 自分自身へのリンクの抑止
 * @graph-connects TermSectionBadge [calls] 種別の札
 * @graph-connects TermRefText [calls] 参照 1 件の名前（単位）
 * @graph-connects TermRefLink [calls] 用語を指す参照 1 件のリンク（属するクラス）
 * @graph-connects TermValueLinks [calls] 取りうる値の並び
 * @graph-connects TermTransitionList [calls] 状態遷移の表
 * @graph-connects termRefCompareKey [calls] 参照 1 件の比べ方
 */
export function termFieldSpecs(t: Translate, pj: string | undefined): readonly TermFieldSpec[] {
  const valuesNote = t('termDetail.valuesOrderNote');
  return [
    {
      key: 'label',
      label: t('termDetail.nameTitle'),
      compareKey: (detail) => detail.label,
      render: (detail) => displayLabel(detail.label, t, detail.untranslated),
    },
    {
      key: 'description',
      label: t('termDetail.definitionTitle'),
      compareKey: (detail) => detail.description,
      render: (detail) =>
        detail.description === '' ? (
          <span className="text-[var(--color-text-muted)]">{t('termDetail.definitionEmpty')}</span>
        ) : (
          <TermLinkSuppress termId={String(detail.termId)}>
            <TermText text={detail.description} />
          </TermLinkSuppress>
        ),
    },
    {
      key: 'note',
      label: t('termDetail.noteTitle'),
      compareKey: (detail) => detail.note ?? '',
      render: (detail) =>
        detail.note === null ? (
          <span className="text-[var(--color-text-muted)]">{t('termDetail.noteEmpty')}</span>
        ) : (
          <div className="whitespace-pre-wrap" data-term-note>
            <TermLinkSuppress termId={String(detail.termId)}>
              <TermText text={detail.note} />
            </TermLinkSuppress>
          </div>
        ),
    },
    {
      key: 'section',
      label: t('termDetail.sectionTitle'),
      compareKey: (detail) => detail.section ?? '',
      render: (detail) => <TermSectionBadge section={detail.section} />,
    },
    {
      key: 'representation',
      label: t('termDetail.typeTitle'),
      compareKey: (detail) => detail.representation ?? '',
      render: (detail) => {
        const key = termEnumKey('termRepresentation', detail.representation);
        return key === null ? (
          <span className="text-[var(--color-text-muted)]">{t('termDetail.unset')}</span>
        ) : (
          t(key)
        );
      },
    },
    {
      key: 'ownerClass',
      label: t('termDetail.ownerClassTitle'),
      compareKey: (detail) => termRefCompareKey(detail.ownerClass),
      render: (detail) => <TermRefLink refValue={detail.ownerClass} pj={pj} />,
    },
    {
      key: 'unit',
      label: t('termDetail.unitTitle'),
      compareKey: (detail) => termRefCompareKey(detail.unit),
      render: (detail) => <TermRefText refValue={detail.unit} />,
    },
    {
      key: 'values',
      label: (
        <span className="inline-flex items-center gap-1">
          {t('termDetail.valuesTitle')}
          <span
            title={valuesNote}
            aria-label={valuesNote}
            data-values-order-note
            className="cursor-help"
          >
            ⓘ
          </span>
        </span>
      ),
      compareKey: (detail) => detail.values.map(termRefCompareKey).join('|'),
      render: (detail) =>
        detail.values.length === 0 ? (
          t('termDetail.none')
        ) : (
          <TermValueLinks values={detail.values} pj={pj} />
        ),
    },
    {
      key: 'transitions',
      label: t('termDetail.transitionsTitle'),
      compareKey: (detail) =>
        detail.transitions
          .map((transition) =>
            [transition.from, transition.event, transition.to].map(termRefCompareKey).join('>'),
          )
          .join('|'),
      render: (detail) => <TermTransitionList transitions={detail.transitions} pj={pj} />,
    },
    {
      key: 'synonyms',
      label: t('termDetail.synonymsTitle'),
      compareKey: (detail) =>
        detail.synonyms.map((synonym) => `${synonym.text}:${synonym.note}`).join('|'),
      render: (detail) =>
        detail.synonyms.length === 0 ? t('termDetail.none') : <TermSynonymList detail={detail} />,
    },
  ];
}

/**
 * 左右の姿のどちらかで書ける欄だけを残す。
 *
 * 書けない欄は出さない（`term-field-applicability.ts`）。左右のどちらかで書ける欄は、変更前後を
 * 比べるために残す。種別か値のタイプが未設定の古い用語は判定できないので、欄を全部出す。
 *
 * @param specs 欄の定義
 * @param sides 左右の姿（案件を選んでいなければ両側に同じ姿）
 * @returns 出す欄の定義
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の種別と値のタイプから、左右のどちらかで書ける欄の定義だけを残す。
 * @graph-connects isTermShapeField [calls] 出し分けの対象の欄かの判定
 * @graph-connects termFieldApplies [calls] 種別と値のタイプで書ける欄かの判定
 */
export function applicableTermFields(
  specs: readonly TermFieldSpec[],
  sides: PjSides<TermDetailOutput>,
): TermFieldSpec[] {
  return specs.filter((spec) => {
    const key = spec.key;
    return (
      !isTermShapeField(key) ||
      [sides.before, sides.after].some((detail) => detail !== null && termFieldApplies(key, detail))
    );
  });
}

/**
 * 1 面の表の 1 行。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の基本情報の 1 面の表の 1 行（欄・値・案件で変わったか）を表す。
 * @graph-connects none
 */
export interface TermInfoRow {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** 案件で変わった（足した・消したを含む）欄か。 */
  changed: boolean;
}

/** 表のセルの共通 class。 @graph-connects none */
const INFO_CELL_CLASS = 'border border-[var(--color-border)] px-3 py-2 text-left align-top text-sm';

/** `TermInfoTable` の props。 @graph-connects none */
export interface TermInfoTableProps {
  rows: readonly TermInfoRow[];
  /** 値がどちら側の姿か。変わった欄の地色を決める。案件で消した用語だけ `before`。 */
  side: DiffSide;
}

/**
 * 基本情報の 1 面の表。列は 欄 ／ 値。
 *
 * **案件で変わった欄は、値のセルに地色を敷く**（業務詳細の 1 面の表と同じ `specDiffCellClass`）。
 * 札を足さない — セルの色で読める（Dhik 2026-09-29「セル内の変わったところの色を濃くするで統一」）。
 *
 * @param props 行と、値がどちら側の姿か
 * @returns 欄 ／ 値 の表
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の基本情報を欄 ／ 値 の 2 列の表で描き、案件で変わった欄の値のセルに地色を敷く。
 * @graph-connects specDiffCellClass [calls] 変わったセルの地色
 */
export function TermInfoTable({ rows, side }: TermInfoTableProps) {
  const { t } = useTranslation();
  return (
    <table className="w-full table-fixed border-collapse" data-term-info>
      <thead>
        <tr>
          <th
            scope="col"
            className={`${INFO_CELL_CLASS} w-40 text-xs font-bold text-[var(--color-text-muted)]`}
          >
            {t('pjCompare.fieldColumn')}
          </th>
          <th
            scope="col"
            className={`${INFO_CELL_CLASS} text-xs font-bold text-[var(--color-text-muted)]`}
          >
            {t('termDetail.valueColumn')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} data-field={row.key} data-changed={row.changed}>
            <th
              scope="row"
              className={`${INFO_CELL_CLASS} text-xs font-normal text-[var(--color-text-muted)]`}
            >
              {row.label}
            </th>
            <td
              className={`${INFO_CELL_CLASS} text-[var(--color-text-primary)]${specDiffCellClass(row.changed, side)}`}
            >
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** `TermValueLinks` の props。 @graph-connects none */
interface TermValueLinksProps {
  values: readonly TermRef[];
  /** いま重ねている案件番号。マスターを見ていれば undefined。 */
  pj: string | undefined;
}

/**
 * 取りうる値の用語へのリンクの並び。**並びの順に意味がある**ので、宣言の順のまま出す。
 *
 * **値の用語へも案件を引き継ぐ。** 落とすと、案件を適用した姿から押した先だけマスターになる。
 * 案件でしか存在しない値の用語（実測で 1410〜1417 の 8 件）はそこに居ないので「見つかりません」
 * に着き、落ちたことは画面に出ない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の取りうる値を宣言の順のまま、案件を引き継いだリンクの札で並べる。
 * @graph-connects TERM_DETAIL_ROUTE [references] 値の用語の詳細
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 */
function TermValueLinks({ values, pj }: TermValueLinksProps) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-wrap gap-2" data-term-values>
      {values.map((value) => (
        <li key={value.id}>
          <Link to={TERM_DETAIL_ROUTE} params={{ termId: String(value.id) }} {...pjLinkSearch(pj)}>
            <Badge variant="info" size="sm" data-id={value.id}>
              {displayLabel(value.label, t)}
            </Badge>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * 揺れ表記の並び。注記があれば括弧で添える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の揺れ表記を注記つきで並べる。
 * @graph-connects none
 */
function TermSynonymList({ detail }: { detail: TermDetailOutput }) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-wrap items-center gap-2" data-term-synonyms>
      {detail.synonyms.map((synonym) => (
        <li key={synonym.text}>
          <Badge variant="info" size="sm">
            {synonym.text}
          </Badge>
          {synonym.note !== '' && (
            <span className="ml-1 text-xs text-[var(--color-text-muted)]">
              {t('termDetail.synonymNote', { note: synonym.note })}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** `TermRefText` の props。 @graph-connects none */
export interface TermRefTextProps {
  refValue: TermRef | null;
}

/**
 * 参照 1 件を名前で描く。参照が無い、または名前の材料が無ければ「未設定」。
 *
 * **番号を添えない。** 人は番号で要素を判別できない。番号は `data-id` にだけ残す（調べる人は
 * 開発者ツールで引ける）。
 *
 * @param props 参照 1 件
 * @returns 参照先の名前
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語が指す参照 1 件を名前で描き、未設定なら「未設定」と出す。番号は出さない。
 * @graph-connects displayLabel [calls] 参照先の名前の穴埋め
 */
export function TermRefText({ refValue }: TermRefTextProps) {
  const { t } = useTranslation();
  if (refValue === null) {
    return <span className="text-[var(--color-text-muted)]">{t('termDetail.unset')}</span>;
  }
  return <span data-id={refValue.id}>{displayLabel(refValue.label, t)}</span>;
}

/** `TermRefLink` の props。 @graph-connects none */
export interface TermRefLinkProps {
  /** 用語を指す参照 1 件。未設定なら null */
  refValue: TermRef | null;
  /** いま重ねている案件番号。マスターを見ていれば undefined。 */
  pj: string | undefined;
}

/**
 * 用語を指す参照 1 件を、その用語の詳細へのリンクで描く。参照が無ければ「未設定」の文字にする。
 *
 * **用語を指す参照だけに使う**（属するクラス・状態遷移の前後の値）。単位と出来事（動詞）は用語では
 * ないので `TermRefText` で名前だけを出す — 用語詳細へ送ると別の種類の番号で開き「見つかりません」に着く。
 *
 * **案件を引き継ぐ**（取りうる値のリンクと同じ）。落とすと、案件でしか存在しない用語は
 * 「見つかりません」に着き、落ちたことは画面に出ない。
 *
 * @param props 参照 1 件と案件番号
 * @returns 用語詳細へのリンク、または「未設定」
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語を指す参照 1 件を、案件を引き継いだ用語詳細へのリンクで描き、未設定なら「未設定」と出す。
 * @graph-connects TERM_DETAIL_ROUTE [references] 参照先の用語の詳細
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 * @graph-connects TermRefText [calls] 未設定の表示
 * @graph-connects displayLabel [calls] 参照先の名前の穴埋め
 */
export function TermRefLink({ refValue, pj }: TermRefLinkProps) {
  const { t } = useTranslation();
  if (refValue === null) return <TermRefText refValue={null} />;
  return (
    <Link
      to={TERM_DETAIL_ROUTE}
      params={{ termId: String(refValue.id) }}
      {...pjLinkSearch(pj)}
      data-id={refValue.id}
      className="text-[var(--color-accent-primary)] underline"
    >
      {displayLabel(refValue.label, t)}
    </Link>
  );
}

/** 状態遷移の表のセルの class。 @graph-connects none */
const TRANSITION_CELL_CLASS = 'border-b border-[var(--color-border)] px-3 py-1.5 text-left';

/** `TermTransitionList` の props。 @graph-connects none */
export interface TermTransitionListProps {
  transitions: readonly TermTransition[];
  /** いま重ねている案件番号。前後の値のリンクへ引き継ぐ。マスターを見ていれば undefined。 */
  pj: string | undefined;
}

/**
 * 状態遷移の表。列は 今の値 ／ 出来事（動詞）／ 次の値 で、1 行が 1 本。
 *
 * 3 つ組のまま出すのは、遷移が**その 3 つで初めて意味を持つ**ためである（`derive-edges.ts` が
 * 遷移を辺に潰さない理由と同じ）。今の値と次の値は用語なので用語詳細へのリンクにし（案件を
 * 引き継ぐ）、出来事は動詞なので名前だけを出す。
 *
 * @param props 状態遷移と案件番号
 * @returns 状態遷移の表
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の状態遷移を、今の値・出来事（動詞）・次の値の 3 列の表で宣言の順に並べ、前後の値を用語詳細へのリンクにし、0 件なら未設定と出す。
 * @graph-connects TermRefText [calls] 出来事（動詞）の名前
 * @graph-connects TermRefLink [calls] 今の値・次の値の用語へのリンク
 */
export function TermTransitionList({ transitions, pj }: TermTransitionListProps) {
  const { t } = useTranslation();
  return (
    <table className="w-full border-collapse text-sm" data-transitions>
      <thead>
        <tr className="text-xs text-[var(--color-text-muted)]">
          <th scope="col" className={TRANSITION_CELL_CLASS}>
            {t('termDetail.transitionFrom')}
          </th>
          <th scope="col" className={TRANSITION_CELL_CLASS}>
            {t('termDetail.transitionEvent')}
          </th>
          <th scope="col" className={TRANSITION_CELL_CLASS}>
            {t('termDetail.transitionTo')}
          </th>
        </tr>
      </thead>
      <tbody>
        {transitions.length === 0 && (
          <tr>
            <td colSpan={3} className={`${TRANSITION_CELL_CLASS} text-[var(--color-text-muted)]`}>
              {t('termDetail.transitionsEmpty')}
            </td>
          </tr>
        )}
        {transitions.map((transition, index) => (
          <tr key={index}>
            <td className={TRANSITION_CELL_CLASS}>
              <TermRefLink refValue={transition.from} pj={pj} />
            </td>
            <td className={TRANSITION_CELL_CLASS}>
              <TermRefText refValue={transition.event} />
            </td>
            <td className={TRANSITION_CELL_CLASS}>
              <TermRefLink refValue={transition.to} pj={pj} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
